/**
 * optionalImport — NOVA-ENV-1 (Oct 12). A dynamic import that is allowed
 * to fail.
 *
 * WHY IT EXISTS. DEPLOY-1 (src/main.tsx) answers every `vite:preloadError`
 * with a hard reload, so a tab holding a stale index.html heals itself.
 * That is right for a route: without its chunk there is no page. It is
 * wrong for an extra the page can live without. /nova/environment asks
 * for the 3D Nova and the Earth on every load, and if either chunk could
 * not be fetched (offline, a blocked CDN path) the reload would only ask
 * again, forever — the hazard SeasonalSky documents, and the reason it
 * fetches its paintings as JSON. Three.js cannot be fetched as JSON, so
 * these imports say what they are instead.
 *
 *   optionalImport(() => import("@/lib/nova3d/novaEngine"))
 *     .then(({ createNova }) => { … })
 *     .catch(showTheStills);
 *
 * Keep the `import("…")` literal inside the arrow: that is what Vite
 * splits into its own chunk.
 *
 * ONLY FOR CHUNKS WITH NO STYLESHEET OF THEIR OWN. Vite's loader remembers
 * every stylesheet it has tried and never tries one again. If a chunk's
 * CSS failed inside an optional import, nothing would reload, and a later
 * required import of the same chunk would load its code without its
 * styles. The two engines are plain JavaScript, so they are safe. A page
 * (a route, anything that imports .css) is not: do not prefetch one this
 * way.
 *
 * HOW main.tsx TELLS THE TWO APART. Vite's event does not say which
 * import failed. So while one of these is in flight, main.tsx lets the
 * rejection through and looks again a moment later: a failure that
 * reached one of these promises is recorded here and left to its caller;
 * any other failure still gets DEPLOY-1's reload. A route that fails at
 * the same moment as an optional import is therefore still healed.
 *
 * A failure is recorded by identity. One that is not an object (a chunk
 * that throws a string, say) has no identity to record, so it is
 * remembered by value for a second: long enough for main.tsx's look, and
 * without it that one failure would be taken for a route's and reloaded,
 * every time the page came back.
 */

let inFlight = 0;
const settledHere = new WeakSet<object>();
const settledValues: unknown[] = [];

const hasIdentity = (err: unknown): err is object =>
  (typeof err === "object" && err !== null) || typeof err === "function";

export function optionalImport<T>(load: () => Promise<T>): Promise<T> {
  inFlight += 1;
  let pending: Promise<T>;
  try {
    pending = load();
  } catch (err) {
    pending = Promise.reject(err);
  }
  return pending.then(
    (mod) => {
      inFlight -= 1;
      return mod;
    },
    (err: unknown) => {
      inFlight -= 1;
      if (hasIdentity(err)) settledHere.add(err);
      else {
        settledValues.push(err);
        setTimeout(() => settledValues.shift(), 1000); // first in, first out: each timer drops its own
      }
      throw err;
    },
  );
}

/** An optional import has been asked for and has not settled yet. */
export const optionalImportInFlight = (): boolean => inFlight > 0;

/** This failure belonged to an optional import, and its caller has it. */
export const isOptionalImportError = (err: unknown): boolean =>
  hasIdentity(err) ? settledHere.has(err) : settledValues.includes(err);
